/**
 * Leaderboard client.
 *
 * The board itself is public — it is the first thing on the splash, before
 * anybody has signed in. Only a handle, a total and a level count come back;
 * the server never sends a name or an address to it.
 */

const TOKEN_KEY = 'devtool-detective.session';

export interface BoardRow {
  rank: number;
  handle: string;
  total: number;
  levels: number;
}

export interface Standing {
  rank: number;
  handle: string;
  total: number;
  levels: number;
  of: number;
}

export interface BoardPayload {
  board: BoardRow[];
  entrants: number;
  /** Present only when signed in. */
  you?: Standing | null;
  handle?: string;
}

function token(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Fetch the board.
 *
 * Sends the session when there is one, purely so the reply can include where
 * the signed-in player stands. Without it the board still loads.
 */
export async function fetchBoard(): Promise<BoardPayload | null> {
  const bearer = token();
  try {
    const response = await fetch('/api/leaderboard', {
      headers: bearer ? { Authorization: `Bearer ${bearer}` } : {},
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { ok?: boolean } & BoardPayload;
    if (!body?.ok) return null;
    return {
      board: Array.isArray(body.board) ? body.board : [],
      entrants: Number(body.entrants) || 0,
      you: body.you ?? null,
      handle: body.handle ?? '',
    };
  } catch {
    return null;
  }
}

/** Set, change or clear the handle. An empty string takes you off the board. */
export async function saveHandle(
  handle: string,
): Promise<{ ok: boolean; handle?: string; error?: string }> {
  const bearer = token();
  if (!bearer) return { ok: false, error: 'Sign in first.' };
  try {
    const response = await fetch('/api/me/handle', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${bearer}` },
      body: JSON.stringify({ handle }),
    });
    const body = (await response.json().catch(() => null)) as
      | { ok?: boolean; handle?: string; error?: string }
      | null;
    if (!response.ok || !body?.ok) {
      return { ok: false, error: body?.error ?? 'Could not save that handle.' };
    }
    return { ok: true, handle: body.handle ?? '' };
  } catch {
    return { ok: false, error: 'Could not reach the server.' };
  }
}
